import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { createTheme, responsiveFontSizes } from '@mui/material/styles'
import { createElement } from 'react'

const isDev = import.meta.env.MODE !== 'production'
const GREY_300 = '#cbd5e1'
const GREY_400 = '#a1adbb'
const BUTTON_TRANSITION_OPTIONS = {
  duration: '0.15s',
  easing: 'cubic-bezier(.4, 0, .2, 1)',
}
const OUTLINED_BUTTON_SHADOW = '0 1px 2px 0 #0000000d'
const PRIMARY_BUTTON_SHADOW = '0 1px 3px 0 #0000001a, 0 1px 2px -1px #0000001a'

const getFormControlTransition = (theme) =>
  theme.transitions.create(
    ['background-color', 'box-shadow', 'border-color', 'color'],
    { duration: theme.transitions.duration.short },
  )

let theme = createTheme({
  palette: {
    primary: {
      main: '#01173c',
      dark: '#000F2B',
      light: '#072C6B',
      contrastText: '#fff',
    },
    secondary: {
      main: '#31d1d3',
      dark: '#26b5b6',
      light: '#56DFDF',
      contrastText: '#000C1F',
    },
    success: {
      main: '#30b97a',
      dark: '#249d68',
      light: '#4dd495',
      contrastText: '#ffffff',
    },
    error: {
      main: '#e54848',
      dark: '#c73838',
      light: '#f16d6d',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#f5b942',
      dark: '#d89d2e',
      light: '#f9cd6e',
      contrastText: '#000C1F',
    },
    info: {
      main: '#2777c6',
      dark: '#1d5e9e',
      light: '#5b9de1',
      contrastText: '#ffffff',
    },
    grey: {
      50: '#fafafa',
      100: '#f9fAfB',
      200: '#e5e7eb',
      300: GREY_300,
      400: GREY_400,
      500: '#65758b',
      600: '#55606c',
      700: '#3b4450',
      800: '#2b323d',
      900: '#1c1f25',
      1000: '#65758b',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    divider: '#e3e7ee',
    text: {
      primary: '#000F2B',
      secondary: '#65758b',
      disabled: GREY_400,
      placeholder: '#65758b',
    },
  },
  typography: {
    fontSize: 14,
    htmlFontSize: 16,
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    body1: {
      fontSize: '1rem',
    },
    h1: {
      letterSpacing: '-0.9px',
      fontWeight: 700,
      color: '#01173c',
    },
    h2: {
      letterSpacing: '-0.7px',
      color: '#01173c',
    },
    h3: {
      letterSpacing: '-0.6px',
      color: '#01173c',
    },
    h4: {
      letterSpacing: '-0.5px',
      color: '#01173c',
      fontWeight: 600,
    },
    h5: {
      letterSpacing: '-0.5px',
      color: '#01173c',
      fontWeight: 600,
    },
    h6: {
      letterSpacing: '-0.5px',
      fontWeight: 600,
      color: '#01173c',
    },
    subtitle1: {
      letterSpacing: '-0.4px',
    },
    subtitle2: {
      letterSpacing: '-0.35px',
      fontWeight: 600,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '::selection': {
          backgroundColor: '#6c82a0',
        },
        body: {
          overflow: 'hidden',
          WebkitFontSmoothing: 'auto',
          MozOsxFontSmoothing: 'auto',
        },
        ':root': {
          '--safe-area-bottom': 'env(safe-area-inset-bottom, 0px)',
        },
        '@media (max-width: 900px)': {
          ':root': {
            '--safe-area-bottom': isDev
              ? '0px'
              : 'env(safe-area-inset-bottom, 0px)',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: ({ theme }) => ({
          '&::placeholder': {
            color: theme.palette.text.placeholder,
            opacity: 1,
          },
          '&.Mui-disabled::placeholder': {
            color: theme.palette.text.disabled,
          },
        }),
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiSvgIcon-root': {
            color: theme.palette.primary.main,
          },
          '.Mui-disabled & .MuiSvgIcon-root': {
            color: theme.palette.text.disabled,
          },
        }),
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: createElement(KeyboardArrowDownIcon, {
          fontSize: 'small',
        }),
      },
      styleOverrides: {
        clearIndicator: ({ theme }) => ({
          color: theme.palette.primary.main,
          '&.Mui-disabled': {
            color: theme.palette.text.disabled,
          },
        }),
        popupIndicator: ({ theme }) => ({
          color: theme.palette.primary.main,
          '&.Mui-disabled': {
            color: theme.palette.text.disabled,
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: ({ theme }) => ({
          transition: getFormControlTransition(theme),
        }),
        root: ({ theme }) => ({
          '&:not(.Mui-error):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline':
            {
              borderColor: theme.palette.grey[300],
            },
          '&:not(.Mui-error):not(.Mui-disabled):hover .MuiOutlinedInput-notchedOutline':
            {
              borderColor: theme.palette.primary.main,
            },
          '&.Mui-focused:not(.Mui-error):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline':
            {
              borderColor: theme.palette.primary.main,
              borderWidth: 1,
            },
        }),
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: KeyboardArrowDownIcon,
      },
      styleOverrides: {
        icon: ({ theme }) => ({
          color: theme.palette.primary.main,
          '&.Mui-disabled': {
            color: theme.palette.text.disabled,
          },
        }),
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        notchedOutline: ({ theme }) => ({
          transition: getFormControlTransition(theme),
        }),
        root: ({ theme }) => ({
          '&:not(.Mui-error):not(.Mui-disabled) .MuiPickersOutlinedInput-notchedOutline':
            {
              borderColor: theme.palette.grey[300],
            },
          '&:not(.Mui-error):not(.Mui-disabled):hover .MuiPickersOutlinedInput-notchedOutline':
            {
              borderColor: theme.palette.primary.main,
            },
          '&.Mui-focused:not(.Mui-error):not(.Mui-disabled) .MuiPickersOutlinedInput-notchedOutline':
            {
              borderColor: theme.palette.primary.main,
              borderWidth: 1,
            },
        }),
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          letterSpacing: 'normal',
          transition: theme.transitions.create(
            ['background-color', 'box-shadow', 'border-color', 'color'],
            BUTTON_TRANSITION_OPTIONS,
          ),
          '&.Mui-disabled': {
            color: theme.palette.text.disabled,
          },
        }),
        sizeSmall: {
          borderRadius: 6,
          fontSize: 12,
        },
        contained: {
          boxShadow: PRIMARY_BUTTON_SHADOW,
          '&:hover': {
            boxShadow: PRIMARY_BUTTON_SHADOW,
          },
        },
        containedPrimary: ({ theme }) => ({
          '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.primary.main,
          },
        }),
        containedSizeMedium: ({ theme }) => ({
          height: 36,
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
        }),
        containedSizeSmall: ({ theme }) => ({
          height: 32,
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
        }),
        outlined: {
          boxShadow: OUTLINED_BUTTON_SHADOW,
          '&:hover': {
            boxShadow: OUTLINED_BUTTON_SHADOW,
          },
        },
        outlinedPrimary: ({ theme }) => ({
          borderColor: theme.palette.primary.main,
          '&:hover': {
            backgroundColor: theme.palette.secondary.main,
          },
        }),
        outlinedSizeMedium: ({ theme }) => ({
          height: 36,
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
          borderColor: theme.palette.primary.main,
        }),
        outlinedSizeSmall: ({ theme }) => ({
          height: 32,
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
          borderColor: theme.palette.primary.main,
        }),
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          '.MuiOutlinedInput-root &:not(.Mui-disabled), .MuiPickersInputBase-root &:not(.Mui-disabled)':
            {
              color: theme.palette.primary.main,
            },
          '&.Mui-disabled': {
            color: theme.palette.text.disabled,
            '& svg': {
              color: theme.palette.text.disabled,
              fill: theme.palette.text.disabled,
            },
          },
        }),
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#2777c6',
          textDecorationColor: 'rgba(39,119,198,0.55)',
          textUnderlineOffset: '2px',
          '&:hover': {
            color: '#1d5e9e',
            textDecorationColor: '#1d5e9e',
          },
          '&:active': {
            color: '#1d5e9e',
            textDecorationColor: '#1d5e9e',
          },
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        slotProps: {
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -4],
                },
              },
            ],
          },
        },
      },
      styleOverrides: {
        tooltip: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
          fontWeight: 400,
        }),
        arrow: ({ theme }) => ({
          color: theme.palette.primary.main,
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(even)': {
            background: '#F6F7F9',
          },
          '&:hover': {
            background: '#E7F0FF !important',
          },
          '&:last-of-type > td': {
            borderBottom: 'none',
          },
          '& a': {
            fontWeight: 500,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          whiteSpace: 'nowrap',
        },
        root: {
          color: '#000F2B',
          whiteSpace: 'pre-line',
          borderBottom: '1px solid rgba(0,0,0,0.07)',
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          '& .MuiBreadcrumbs-separator': {
            margin: '0 4px',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: '#01173c',
          },
          '& a': {
            fontSize: '0.875rem',
            fontWeight: 500,
          },
        },
      },
    },
  },
})

theme = responsiveFontSizes(theme)

export { theme }
